import { h } from '@/lib/dom';
import { Button } from '@/components/Button';
import { SkeletonList } from '@/components/Skeleton';
import { FormField } from '@/components/FormField';
import { toast } from '@/components/Toast';
import { openModal } from '@/components/Modal';
import { data } from '@/lib/data';
import { formatDate, plural } from '@/lib/format';
import { currentSession } from '@/lib/session';
import type { RouteContext } from '@/router';
import { navigate } from '@/router';
import { pageLayout } from './layout';
import type { MedCreate, MedHistoryEntry, MedOut } from '@/api/types';
import { SECTION_ORDER, sectionLabel, sectionEmoji } from '@/lib/med-section';
import type { MedSection } from '@/lib/med-section';

export async function renderMeds(_ctx: RouteContext): Promise<Node> {
  const user = currentSession();
  if (!user) {
    navigate('/login', { replace: true });
    return h('div');
  }
  const isOwner = user.role === 'owner';

  const headerEl = h('header', { class: 'meds-header' }, [
    h('div', { class: 'meds-titles' }, [
      h('h1', { class: 'meds-title' }, 'Препараты'),
      h('p', { class: 'meds-subtitle' }, ' '),
    ]),
    isOwner
      ? Button({
          label: 'Добавить препарат',
          variant: 'secondary',
          icon: '+',
          onClick: () => openMedForm(null, refresh),
        })
      : null,
  ]);

  const activeEl = h('div', { class: 'meds-active' }, SkeletonList(3));
  const archiveEl = h('div', { class: 'meds-archive' });

  const root = pageLayout({
    user,
    active: 'meds',
    children: [
      h('div', { class: 'meds-page' }, [headerEl, activeEl, archiveEl]),
    ],
  });

  async function refresh(): Promise<void> {
    activeEl.innerHTML = '';
    archiveEl.innerHTML = '';
    try {
      const [activeMeds, allMeds] = await Promise.all([
        data.listMeds(false),
        data.listMeds(true),
      ]);
      const archived = allMeds.filter((m) => m.stopped_at !== null);

      const subtitleEl = headerEl.querySelector('.meds-subtitle') as HTMLElement;
      subtitleEl.textContent = activeMeds.length
        ? `${activeMeds.length} ${plural(activeMeds.length, ['активный препарат', 'активных препарата', 'активных препаратов'])}`
        : 'Активных препаратов нет';

      if (!activeMeds.length) {
        activeEl.appendChild(h('div', { class: 'empty-state' }, [
          h('p', null, 'Препаратов не задано.'),
          isOwner ? Button({ label: 'Добавить препарат', onClick: () => openMedForm(null, refresh) }) : null,
        ]));
      } else {
        activeEl.appendChild(groupedMedsList(activeMeds, isOwner, refresh));
      }

      if (archived.length) {
        archiveEl.appendChild(buildArchive(archived, isOwner, refresh));
      }
      document.dispatchEvent(new Event('route:settled'));
    } catch (err) {
      activeEl.innerHTML = '';
      activeEl.appendChild(h('div', { class: 'empty-state', role: 'alert' }, [
        h('p', null, 'Не удалось загрузить препараты.'),
        Button({ label: 'Повторить', variant: 'secondary', onClick: () => void refresh() }),
      ]));
      console.error(err);
    }
  }

  void refresh();
  return root;
}

function toValidSection(s: string | undefined): MedSection {
  const valid = new Set<string>(SECTION_ORDER);
  if (s && valid.has(s)) return s as MedSection;
  return 'other';
}

function groupedMedsList(meds: MedOut[], isOwner: boolean, refresh: () => Promise<void>): HTMLElement {
  const grouped = new Map<MedSection, MedOut[]>();
  for (const m of meds) {
    const sec = toValidSection(m.section);
    if (!grouped.has(sec)) grouped.set(sec, []);
    grouped.get(sec)!.push(m);
  }

  const container = h('div', { class: 'meds-grouped-list' });
  for (const sec of SECTION_ORDER) {
    const list = grouped.get(sec);
    if (!list || !list.length) continue;
    const emoji = sectionEmoji(sec);
    const label = sectionLabel(sec);
    const section = h('section', { class: 'meds-section' }, [
      h('h2', { class: 'meds-section-header' }, [
        emoji ? h('span', { class: 'meds-section-emoji', 'aria-hidden': 'true' }, emoji) : null,
        h('span', null, label),
        h('span', { class: 'meds-section-count' }, String(list.length)),
      ]),
      h('div', { class: 'meds-section-list' },
        list.map((m) => medRow(m, isOwner, refresh, false))),
    ]);
    container.appendChild(section);
  }
  return container;
}

function buildArchive(archived: MedOut[], isOwner: boolean, refresh: () => Promise<void>): HTMLElement {
  const list = h('div', { class: 'meds-section-list' },
    archived.map((m) => medRow(m, isOwner, refresh, true)));
  const summary = h('summary', { class: 'meds-archive-summary' }, [
    h('span', { class: 'meds-archive-chevron', 'aria-hidden': 'true' }, '▸'),
    h('span', null, `Архив · ${archived.length}`),
  ]);
  const details = h('details', { class: 'meds-archive-details' }, [summary, list]);
  return details;
}

function medRow(med: MedOut, isOwner: boolean, refresh: () => Promise<void>, archived: boolean): HTMLElement {
  const metaParts: string[] = [
    `с ${formatDate(med.started_at)}${archived && med.stopped_at ? ` до ${formatDate(med.stopped_at)}` : ''}`,
  ];
  if (med.regimen) metaParts.push(med.regimen);

  return h('div', { class: archived ? 'med-row med-row--archived' : 'med-row' }, [
    h('div', { class: 'med-row-main' }, [
      h('div', { class: 'med-row-name' }, [
        med.name,
        ' ',
        h('span', { class: 'med-row-dose' }, med.dose),
      ]),
      h('div', { class: 'med-row-meta' }, metaParts.join(' · ')),
      med.notes
        ? h('p', { class: 'med-row-notes' }, med.notes)
        : null,
      archived && med.stop_reason
        ? h('p', { class: 'med-row-notes' }, `Причина отмены: ${med.stop_reason}`)
        : null,
      med.dose_changes > 0 ? medHistoryDisclosure(med) : null,
    ]),
    isOwner
      ? h('div', { class: 'med-row-actions' },
          archived
            ? [
                Button({
                  label: 'Вернуть',
                  variant: 'secondary',
                  size: 'sm',
                  onClick: () => resumeMed(med, refresh),
                }),
              ]
            : [
                Button({
                  label: '✎',
                  variant: 'secondary',
                  size: 'sm',
                  ariaLabel: 'Редактировать',
                  onClick: () => openMedForm(med, refresh),
                }),
                Button({
                  label: 'Отменить',
                  variant: 'secondary',
                  size: 'sm',
                  onClick: () => openStopForm(med, refresh),
                }),
              ])
      : null,
  ]);
}

// Collapsible dosage-change log on a med card. The history is fetched lazily
// the first time the disclosure is opened. Entry count = changes + start.
function medHistoryDisclosure(med: MedOut): HTMLElement {
  const count = med.dose_changes + 1;
  const body = h('div', { class: 'med-history-body' }, [
    h('p', { class: 'med-history-loading' }, 'Загрузка…'),
  ]);
  const details = h('details', { class: 'med-history' }, [
    h('summary', { class: 'med-history-summary' }, [
      h('span', { class: 'med-history-chevron', 'aria-hidden': 'true' }, '▸'),
      h('span', null, `История дозировок (${count})`),
    ]),
    body,
  ]) as HTMLDetailsElement;

  let loaded = false;
  details.addEventListener('toggle', () => {
    if (!details.open || loaded) return;
    loaded = true;
    void (async () => {
      try {
        const entries = await data.medHistory(med.id);
        body.innerHTML = '';
        body.appendChild(medHistoryList(entries));
      } catch (err) {
        loaded = false;
        body.innerHTML = '';
        body.appendChild(h('p', { class: 'med-history-error' }, 'Не удалось загрузить историю.'));
        console.error(err);
      }
    })();
  });
  return details;
}

function medHistoryList(entries: MedHistoryEntry[]): HTMLElement {
  return h('ul', { class: 'med-history-list' },
    entries.map((e) =>
      h('li', { class: 'med-history-item' }, [
        h('span', { class: 'med-history-date' }, formatDate(e.date)),
        h('span', { class: 'med-history-change' },
          e.kind === 'start'
            ? `старт: ${e.dose}`
            : `${e.from_dose ?? '—'} → ${e.dose}`),
      ]),
    ),
  );
}

function openMedForm(med: MedOut | null, refresh: () => Promise<void>): void {
  const state: MedCreate = {
    name: med?.name ?? '',
    dose: med?.dose ?? '',
    regimen: med?.regimen ?? '',
    section: med?.section ?? 'breakfast',
    started_at: med?.started_at ?? new Date().toISOString().slice(0, 10),
    notes: med?.notes ?? null,
  };

  const nameInput = h('input', { class: 'form-input', type: 'text', value: state.name, required: true }) as HTMLInputElement;
  const doseInput = h('input', { class: 'form-input', type: 'text', value: state.dose, required: true }) as HTMLInputElement;

  const sectionSelect = h('select', { class: 'form-select', required: true },
    SECTION_ORDER.map((s) => {
      const emoji = sectionEmoji(s);
      const label = sectionLabel(s);
      const text = emoji ? `${emoji} ${label}` : label;
      const opt = h('option', { value: s }, text) as HTMLOptionElement;
      if (s === state.section) opt.selected = true;
      return opt;
    }),
  ) as HTMLSelectElement;

  const regimenInput = h('input', { class: 'form-input', type: 'text', value: state.regimen }) as HTMLInputElement;
  const startedInput = h('input', { class: 'form-input', type: 'date', value: state.started_at, required: true }) as HTMLInputElement;
  const notesInput = h('textarea', { class: 'form-textarea' }, state.notes ?? '') as HTMLTextAreaElement;

  let saving = false;
  openModal({
    title: med ? 'Редактировать препарат' : 'Добавить препарат',
    size: 'md',
    content: (close) => {
      const footer = h('div', { class: 'meds-form-footer flex gap-3 mt-4', style: 'justify-content: flex-end;' });
      const form = h('form', {
        on: {
          submit: async (ev: Event) => {
            ev.preventDefault();
            if (saving) return;
            state.name = nameInput.value.trim();
            state.dose = doseInput.value.trim();
            state.section = sectionSelect.value;
            state.regimen = regimenInput.value.trim();
            state.started_at = startedInput.value;
            state.notes = notesInput.value.trim() || null;
            if (!state.name || !state.dose || !state.started_at) {
              toast('Заполните обязательные поля', { type: 'error' });
              return;
            }
            saving = true;
            try {
              if (med) {
                await data.updateMed(med.id, {
                  name: state.name,
                  dose: state.dose,
                  section: state.section,
                  regimen: state.regimen,
                  started_at: state.started_at,
                  notes: state.notes,
                });
              } else {
                await data.createMed(state);
              }
              toast(med ? 'Препарат обновлён' : 'Препарат добавлен', { type: 'success' });
              close();
              void refresh();
            } catch (err) {
              saving = false;
              toast(err instanceof Error ? err.message : 'Ошибка сохранения', { type: 'error' });
            }
          },
        },
      }, [
        FormField({ label: 'Название', required: true, children: nameInput }),
        FormField({ label: 'Доза', required: true, children: doseInput }),
        FormField({ label: 'Секция приёма', required: true, children: sectionSelect }),
        FormField({ label: 'Комментарий', children: regimenInput }),
        FormField({ label: 'Дата старта', required: true, children: startedInput }),
        FormField({ label: 'Заметка', children: notesInput }),
        footer,
      ]);
      footer.append(
        Button({ label: 'Отмена', variant: 'secondary', onClick: () => close() }),
        Button({ label: med ? 'Сохранить' : 'Добавить', type: 'submit' }),
      );
      return form;
    },
  });
}

async function resumeMed(med: MedOut, refresh: () => Promise<void>): Promise<void> {
  try {
    await data.resumeMed(med.id);
    toast(`${med.name} снова в активных`, { type: 'success' });
    void refresh();
  } catch (err) {
    toast(err instanceof Error ? err.message : 'Ошибка', { type: 'error' });
  }
}

function openStopForm(med: MedOut, refresh: () => Promise<void>): void {
  const stoppedInput = h('input', {
    class: 'form-input',
    type: 'date',
    value: new Date().toISOString().slice(0, 10),
  }) as HTMLInputElement;
  const reasonInput = h('input', {
    class: 'form-input',
    type: 'text',
    placeholder: 'достигнут LDL-C 2.1',
  }) as HTMLInputElement;
  openModal({
    title: 'Отменить препарат',
    size: 'md',
    content: (close) => {
      const footer = h('div', { class: 'meds-form-footer flex gap-3 mt-4', style: 'justify-content: flex-end;' });
      const form = h('form', {
        on: {
          submit: async (ev: Event) => {
            ev.preventDefault();
            try {
              await data.stopMed(med.id, {
                stopped_at: stoppedInput.value,
                reason: reasonInput.value.trim() || null,
              });
              toast(`${med.name} отменён`, { type: 'success' });
              close();
              void refresh();
            } catch (err) {
              toast(err instanceof Error ? err.message : 'Ошибка', { type: 'error' });
            }
          },
        },
      }, [
        h('p', { style: 'font-weight: 500; margin-bottom: 12px;' }, `${med.name} ${med.dose}`),
        FormField({ label: 'Дата отмены', required: true, children: stoppedInput }),
        FormField({ label: 'Причина', children: reasonInput }),
        footer,
      ]);
      footer.append(
        Button({ label: 'Отмена', variant: 'secondary', onClick: () => close() }),
        Button({ label: 'Отменить приём', variant: 'danger', type: 'submit' }),
      );
      return form;
    },
  });
}
